/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html } from 'lit';
import { property, state } from 'lit/decorators.js';
import CDSLink from '@carbon/web-components/es/components/link/link.js';
import HostListener from '@carbon/web-components/es/globals/decorators/host-listener.js';
import HostListenerMixin from '@carbon/web-components/es/globals/mixins/host-listener.js';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import IBM8BarLogoH23 from '../../../es/icons/IBM-8bar-logo--h23.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import styles from './masthead.scss';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The IBM logo UI in masthead.
 *
 * @element c4d-masthead-logo
 */
@customElement(`${c4dPrefix}-masthead-logo`)
class C4DMastheadLogo extends FocusMixin(
  HostListenerMixin(StableSelectorMixin(CDSLink))
) {
  /**
   * Search bar opened flag.
   */
  @state()
  private _hasSearchActive = false;

  /**
   * Handles toggle event from the search component.
   *
   * @param event The event.
   */
  @HostListener('parentRoot:eventToggleSearch')
  // @ts-ignore: The decorator refers to this method but TS thinks this method is not referred to
  private _handleSearchToggle = (event: Event) => {
    if ((event as CustomEvent).detail.active !== undefined) {
      this._hasSearchActive = (event as CustomEvent).detail.active;
    }
  };

  /**
   * `true` to hide the logo at render
   */
  @property({ type: Boolean, reflect: true, attribute: 'hide-logo' })
  hideLogo = false;

  /**
   * Link `href`.
   */
  @property()
  href = (this.constructor as typeof C4DMastheadLogo).hrefDefault;

  /**
   * The shadow slot this logo UI should be in.
   */
  @property({ reflect: true })
  slot = 'brand';

  /**
   * The custom logo path, if it's enabled
   */
  @property({ type: String, attribute: false })
  customLogoPath = '';

  /**
   * The custom logo alternative text, if it's enabled
   */
  @property({ type: String, attribute: false })
  customLogoAlt = '';

  /**
   * The custom logo href, if it's enabled
   */
  @property({ type: String, attribute: false })
  customLogoHref = '';

  /**
   * If c4d-masthead-container has the attribute 'custom-logo-override', it enables the custom logo and all its custom info
   */
  private hasCustomLogo() {
    const mastheadContainerWithCustomLogo = this.closest(
      'c4d-masthead-container[custom-logo-override]'
    );

    if (!mastheadContainerWithCustomLogo) {
      return;
    }

    const path = mastheadContainerWithCustomLogo.getAttribute(
      'custom-logo-override'
    );
    const alt = mastheadContainerWithCustomLogo.getAttribute('custom-logo-alt');
    const href =
      mastheadContainerWithCustomLogo.getAttribute('custom-logo-href');

    if (path) {
      this.customLogoPath = path;
    }
    if (alt) {
      this.customLogoAlt = alt;
    }
    if (href) {
      this.href = href;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  protected _renderInner() {
    return html`
      <slot>
        ${this.customLogoPath
          ? html`<img
              class="custom-logo-override"
              src="${this.customLogoPath}"
              alt="${this.customLogoAlt || 'Logo'}"
              part="custom-logo-masthead" />`
          : IBM8BarLogoH23()}
      </slot>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.hasCustomLogo();
  }

  updated(changedProperties) {
    const { _linkNode: linkNode } = this;

    if (changedProperties.has('hideLogo')) {
      this._hasSearchActive = this.hideLogo;
    }
    if (linkNode) {
      if (this.customLogoAlt) {
        linkNode.setAttribute('aria-label', `${this.customLogoAlt}`);
      } else {
        linkNode.setAttribute('aria-label', 'IBM logo');
      }

      linkNode.classList.remove(`${prefix}--link`);
      linkNode.classList.toggle(
        `${c4dPrefix}-ce--header__logo--has-search-active`,
        this._hasSearchActive
      );
    }
  }

  /**
   * The default value to use for hrefs.
   */
  static get hrefDefault() {
    return 'https://www.ibm.com/';
  }

  /**
   * The name of the custom event fired after the seach is toggled.
   */
  static get eventToggleSearch() {
    return `${c4dPrefix}-search-with-typeahead-toggled`;
  }

  static get stableSelector() {
    return `${c4dPrefix}--masthead-logo`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DMastheadLogo;
